import * as React from 'react'
import { BrowserRouter as Router, Route, Switch, Redirect, useHistory, useParams } from 'react-router-dom'
import { HelpCenterMessengerEventType, HelpCenterTheme, PublicLocale } from '../../types'
import i18next from 'i18next'
import ERoute from '../ERoute'
import Header from '../components/Header/Header'
import Overview from './Overview'
import styled, { createGlobalStyle, css } from 'styled-components'
import Collection from './Collection'
import Article from './Article'
import { useDispatch, useSelector } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { AppState } from '../store'
import { overviewLink } from '../helpers/NavigationHelper'
import HelpCenterHelper from '../../helpers/HelpCenterHelper'
import { HelpCenterActionTypes } from '../store/help-center/types'
import { WidgetHelper } from '../../widget/helpers'
import { TooltipProvider } from '@/components/ui/tooltip'

const Wrapper = styled.div<{ embedded: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 40px;

  ${props => !props.embedded && css`
    @media screen and (max-width: 767px) {
      padding: 0 20px;
    }
  `}

  ${props => props.embedded && css`
    @media screen and (max-width: 600px) {
      padding: 0 20px;
    }
  `}
`

const Content = styled.div<{ embedded: boolean }>`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding-top: 36px;
  padding-bottom: 36px;

  ${(props) => props.embedded && css`
    padding: 0;
  `}
`

const GlobalStyle = createGlobalStyle<{ theme: HelpCenterTheme }>`
  body {
    background-color: ${(props) => props.theme.colors.body};
  }

  a {
    color: ${(props) => props.theme.colors.action};
  }
`

interface IProps { }

const App = (props: IProps) => {
  const { currentLocale, helpCenter } = useSelector((state: AppState) => state.helpCenter)
  const dispatch = useDispatch() as Dispatch<AnyAction>
  const embedded = HelpCenterHelper.isEmbedded()

  const onLinkClick = (e) => {
    const target = e.target as HTMLElement

    // Check if clicked element is a link or inside a link
    const link = target.closest('a')


    if (link?.href && !link.href.includes('javascript:')) {
      if (link.href.includes(helpCenter.url)) {
        const url = link.href

        if (WidgetHelper.isDeepLink(url)) {
          e.preventDefault()

          HelpCenterHelper.sendParentMessage({
            type: HelpCenterMessengerEventType.DEEP_LINK,
            url: url
          })
        } else {
          throw new Error(`Unknown deep link format: ${url}`)
        }
      } else {
        e.preventDefault()
        window.open(link.href, '_blank')
      }
    }
  }

  React.useEffect(() => {
    i18next.changeLanguage(currentLocale, (err, t) => { });
  }, [currentLocale])

  React.useEffect(() => {
    const locale = HelpCenterHelper.getLocaleFromUrl()
    const action: HelpCenterActionTypes = { type: 'SET_LOCALE', locale: (locale || helpCenter.locale) as PublicLocale }

    dispatch(action)
  }, [window.location.href])

  React.useEffect(() => {
    if (HelpCenterHelper.isEmbedded()) {
      window.addEventListener('click', onLinkClick)

      return () => {
        window.removeEventListener('click', onLinkClick)
      }
    }

  }, [])

  return (
    <TooltipProvider>
      <Router>
        <GlobalStyle />
        <Header />

        <Wrapper embedded={embedded}>
          <Content embedded={embedded}>
            <Switch>
              <Route path={ERoute.PATH_COLLECTION} component={Collection} />
              <Route path={ERoute.PATH_ARTICLE} component={Article} />
              <Route path={ERoute.PATH_ROOT} component={Overview} />
              <Redirect to={overviewLink(currentLocale)} />
            </Switch>
          </Content>
        </Wrapper>
      </Router>
    </TooltipProvider>
  )
}

export default App